import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';

import { CoverageType, IBenefitsInformation } from '@sondermind/data-access/client-insurance';

import { ITermDefinitions } from '../../models/cost-estimation.model';

const COVERAGE_TYPE = CoverageType;

@Component({
  selector: 'client-cost-estimation-faq',
  templateUrl: './cost-estimation-faq.component.html',
  styleUrls: ['./cost-estimation-faq.component.scss'],
  imports: [
    CommonModule,
    MatExpansionModule,
  ]
})
export class CostEstimationFaqComponent implements OnInit {
  @Input() benefitsInfo!: IBenefitsInformation;

  disclaimerMessage: string = '';
  costCalcExplanation: string[] = [];
  insuranceTermDefinitions: ITermDefinitions[] = [];
  showInitialSessionCostFAQ: boolean = false;
  showInsuranceTermsFAQ: boolean = false;
  deductibleMet: boolean = false;
  deductibleAmount: number = 0;
  outOfPocketMaxMet: boolean = false;
  outOfPocketMaxAmount: number = 0;

  ngOnInit(): void {
    if (this.benefitsInfo?.indDeductibleLimit) {
      this.deductibleMet = this.benefitsInfo?.indDeductibleRemaining?.amount === 0;
      this.deductibleAmount = this.benefitsInfo?.indDeductibleLimit?.amount;
    }

    this.outOfPocketMaxMet = this.benefitsInfo?.indOutOfPocketRemaining?.amount === 0;
    this.outOfPocketMaxAmount = this.benefitsInfo?.indOutOfPocketLimit?.amount;

    this.evaluateCoverageTypeFAQs();
  }

  evaluateCoverageTypeFAQs(): void {
    this.insuranceTermDefinitions = [];

    switch (this.benefitsInfo?.benefitStructure?.name) {
      case COVERAGE_TYPE.COPAY_NO_DEDUCTIBLE_NO_OOP_MAX:
        this.setDisclaimerMessage(false);
        this.showInsuranceTermsFAQ = false;
        this.showInitialSessionCostFAQ = false;
        this.costCalcExplanation = [
          'We reviewed your health insurance plan and found that it has an ongoing copay.',
          `That means for every session, you are only responsible for a 
            <strong>$${this.benefitsInfo.displayedClientCostEstimate}</strong>
            flat fee (copay), while your insurance covers the rest.`
        ];
        break;
      case COVERAGE_TYPE.COPAY_NO_DEDUCTIBLE_WITH_OOP_MAX:
        this.setDisclaimerMessage(false);
        this.getInsuranceTerms(true, false, false, true);
        this.showInitialSessionCostFAQ = false;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>two phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
            Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
            <strong>${this.outOfPocketMaxMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.outOfPocketMaxAmount} out-of-pocket max</strong> 
            for this year, which means you'll pay a 
            <strong>$${this.benefitsInfo.displayedClientCostEstimate} copay per session.</strong>`
        ];
        break;
      case CoverageType.COPAY_AFTER_DEDUCTIBLE_NO_OOP_MAX:
        this.setDisclaimerMessage(!this.deductibleMet);
        this.getInsuranceTerms(true, false, true, false);
        this.showInitialSessionCostFAQ = !this.deductibleMet;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>two phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
              Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
              <strong>${this.deductibleMet ? 'have' : 'have not'}</strong>
              met your 
              <strong>$${this.deductibleAmount} deductible</strong> 
              for this year, which means you'll pay 
              <strong>${this.getCopayAmount()}.</strong>`
        ];
        break;
      case CoverageType.COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX:
        this.setDisclaimerMessage(!this.outOfPocketMaxMet);
        this.getInsuranceTerms(false, true, true, true);
        this.showInitialSessionCostFAQ = !this.outOfPocketMaxMet;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>three phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
            Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
            <strong>${this.deductibleMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.deductibleAmount} deductible</strong> 
            and
            <strong>${this.outOfPocketMaxMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.outOfPocketMaxAmount} out-of-pocket max</strong> 
            for this year, which means you'll pay 
            <strong>${this.getCoinsurancePercentage()}% of session fees.</strong>
            This type of plan is called coinsurance.`
        ];
        break;
      case CoverageType.COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX:
        this.setDisclaimerMessage(true);
        this.getInsuranceTerms(false, true, true, false);
        this.showInitialSessionCostFAQ = true;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>two phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
              Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
              <strong>${this.deductibleMet ? 'have' : 'have not'}</strong>
              met your 
              <strong>$${this.deductibleAmount} deductible</strong> 
              for this year, which means you'll pay 
              <strong>${this.getCoinsurancePercentage()}% of session fees.</strong>
              This type of plan is called coinsurance.`
        ];
        break;
      case COVERAGE_TYPE.COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX:
        this.setDisclaimerMessage(!this.outOfPocketMaxMet);
        this.getInsuranceTerms(false, true, false, true);
        this.showInitialSessionCostFAQ = !this.outOfPocketMaxMet;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>two phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
            Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
            <strong>${this.outOfPocketMaxMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.outOfPocketMaxAmount} out-of-pocket max</strong> 
            for this year, which means you'll pay 
            <strong>${this.getCoinsurancePercentage()}% of session fees.</strong>
            This type of plan is called coinsurance.`
        ];
        break;
      case COVERAGE_TYPE.COINSURANCE_NO_DEDUCTIBLE_NO_OOP_MAX:
        this.setDisclaimerMessage(true);
        this.getInsuranceTerms(false, true, false, false);
        this.showInitialSessionCostFAQ = true;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has an ongoing coinsurance fee.`,
          `That means for every session, you are only responsible for 
              <strong>${this.getCoinsurancePercentage()}% of session fees</strong>, 
              while your insurance covers the rest.`
        ];
        break;
      case COVERAGE_TYPE.COPAY_AFTER_DEDUCTIBLE_WITH_OOP_MAX:
        this.setDisclaimerMessage(!this.deductibleMet && !this.outOfPocketMaxMet);
        this.getInsuranceTerms(true, false, true, true);
        this.showInitialSessionCostFAQ = !this.deductibleMet;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>three phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
            Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
            <strong>${this.deductibleMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.deductibleAmount} deductible</strong> 
            and
            <strong>${this.outOfPocketMaxMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.outOfPocketMaxAmount} out-of-pocket max</strong> 
            for this year, which means you'll pay 
            <strong>${this.getCopayAmount()}.</strong>`
        ];
        break;
      case COVERAGE_TYPE.COPAY_AND_COINSURANCE_NO_DEDUCTIBLE_WITH_OOP_MAX:
        this.setDisclaimerMessage(!this.outOfPocketMaxMet);
        this.getInsuranceTerms(true, true, false, true);
        this.showInitialSessionCostFAQ = !this.outOfPocketMaxMet;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>two phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
          Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
          <strong>${this.outOfPocketMaxMet ? 'have' : 'have not'}</strong>
          met your 
          <strong>$${this.outOfPocketMaxAmount} out-of-pocket max</strong> 
          for this year, which means you'll pay 
          <strong>${this.outOfPocketMaxMet ? '0% of session fees' : this.getCopayAmountAndCoinsurancePercentage()}</strong>`
        ];
        break;
      case COVERAGE_TYPE.COPAY_AND_COINSURANCE_NO_DEDUCTIBLE_NO_OOP_MAX:
        this.setDisclaimerMessage(true);
        this.getInsuranceTerms(true, true, false, false);
        this.showInitialSessionCostFAQ = true;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it involves both a copay and coinsurance.`,
          `For every session, you are responsible for a <strong>$${this.benefitsInfo.displayedClientCoPayAmount}</strong> flat fee (copay), 
            as well as <strong>${this.benefitsInfo.displayedClientCoInsurancePercent * 100}%</strong> of session costs (coinsurance).`,
        ];
        break;
      case COVERAGE_TYPE.COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_WITH_OOP_MAX:
        this.setDisclaimerMessage(!this.outOfPocketMaxMet);
        this.getInsuranceTerms(true, true, true, true);
        this.showInitialSessionCostFAQ = !this.outOfPocketMaxMet;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>three phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
            Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
            <strong>${this.deductibleMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.deductibleAmount} deductible</strong> 
            and
            <strong>${this.outOfPocketMaxMet ? 'have' : 'have not'}</strong>
            met your 
            <strong>$${this.outOfPocketMaxAmount} out-of-pocket max</strong> 
            for this year, which means you'll pay 
            <strong>${this.outOfPocketMaxMet ? '0% of session fees' : this.getCopayAmountAndCoinsurancePercentage()}.</strong>`
        ];
        break;
      case COVERAGE_TYPE.COPAY_AND_COINSURANCE_AFTER_DEDUCTIBLE_NO_OOP_MAX:
        this.setDisclaimerMessage(true);
        this.getInsuranceTerms(true, true, true, false);
        this.showInitialSessionCostFAQ = true;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>two phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
          Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
          <strong>${this.deductibleMet ? 'have' : 'have not'}</strong>
          met your 
          <strong>$${this.deductibleAmount} deductible</strong> 
          for this year, which means you'll pay 
          <strong>${this.getCopayAmountAndCoinsurancePercentage()}.</strong>`
        ];
        break;
      case CoverageType.FULLY_COVERED:
        this.setDisclaimerMessage(false);
        this.showInsuranceTermsFAQ = false;
        this.showInitialSessionCostFAQ = false;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it fully covers all session costs. 
            That means you'll pay <strong>$0</strong> for your sessions!`
        ];
        break;
      case CoverageType.FULLY_COVERED_AFTER_DEDUCTIBLE:
        this.setDisclaimerMessage(!this.deductibleMet);
        this.getInsuranceTerms(false, false, true, false);
        this.showInitialSessionCostFAQ = !this.deductibleMet;
        this.costCalcExplanation = [
          `We reviewed your health insurance plan and found that it has <strong>two phases</strong> of coverage.`,
          `These phases depend on how much you've spent on healthcare during the year. 
              Once you reach certain spending levels, your out-of-pocket costs for therapy sessions will decrease.`,
          `We can see that you 
              <strong>${this.deductibleMet ? 'have' : 'have not'}</strong>
              met your 
              <strong>$${this.deductibleAmount} deductible</strong> 
              for this year, which means you'll pay 
              <strong>${this.deductibleMet ? '0%' : '100%'} of session fees.</strong>`
        ];
        break;
      default:
        this.setDisclaimerMessage(false);
        this.getInsuranceTerms(true, true, true, true);
        this.showInitialSessionCostFAQ = true;
        break;
    }
  }

  getCopayAmount(): string {
    if (!this.deductibleMet && !this.outOfPocketMaxMet) {
      return '100% of session fees';
    }

    if (this.deductibleMet && !this.outOfPocketMaxMet) {
      return `a $${this.benefitsInfo.displayedClientCostEstimate} copay per session`;
    }

    if (this.deductibleMet && this.outOfPocketMaxMet) {
      return `$${this.benefitsInfo.displayedClientCostEstimate} per session`;
    }

    return '';
  }

  getCoinsurancePercentage(): number {
    // Plan has deductbile
    if (this.benefitsInfo?.indDeductibleLimit) {
      if (!this.deductibleMet && !this.outOfPocketMaxMet) {
        return 100;
      }

      if (this.deductibleMet && !this.outOfPocketMaxMet) {
        return this.benefitsInfo.displayedClientCoInsurancePercent * 100;
      }
    }

    // Plan has no deductible
    if (!this.benefitsInfo?.indDeductibleLimit && !this.outOfPocketMaxMet) {
      return this.benefitsInfo.displayedClientCoInsurancePercent * 100;
    }

    return 0;
  }

  getCopayAmountAndCoinsurancePercentage(): string {
    // Plan has deductible, but not met
    if (this.benefitsInfo?.indDeductibleLimit && !this.deductibleMet) {
      return '100% of session fees';
    }

    return `${this.getCoinsurancePercentage()}% of session fees and a 
      $${this.benefitsInfo.displayedClientCoPayAmount} copay`;
  }

  getInsuranceTerms(showCopay: boolean, showCoinsurance: boolean, showDeductible: boolean, showOOP: boolean): void {
    this.showInsuranceTermsFAQ = true;

    if (showCopay) {
      this.insuranceTermDefinitions.push(
        {
          term: 'Copay',
          definition: `A copay is a fixed dollar amount you pay for a specific service, 
            like a therapy session, at the time of your visit. For example, if your copay is $30, 
            you would only pay this amount directly to the therapist, while your insurance covers the rest.`
        }
      );
    }

    if (showCoinsurance) {
      this.insuranceTermDefinitions.push(
        {
          term: 'Coinsurance',
          definition: `Coinsurance is the percentage of the costs you share with your insurance. 
            For instance, if your coinsurance is 20%, you would pay 20% of the therapy session cost 
            while your insurance covers the remaining 80%.`
        }
      );
    }

    if (showDeductible) {
      this.insuranceTermDefinitions.push(
        {
          term: 'Deductible',
          definition: `A deductible is the amount you must pay out-of-pocket towards 
            healthcare services before your insurance begins to cover costs. If your deductible is $1,000, 
            you will pay 100% of healthcare costs until you have paid $1,000 out-of-pocket. 
            Once you meet your deductible, your insurer will start to pay a portion of your healthcare costs.`
        }
      );
    }

    if (showOOP) {
      this.insuranceTermDefinitions.push(
        {
          term: 'Out-of-Pocket Maximum',
          definition: `This is the maximum amount you’ll have to pay for covered services in a plan year. 
          Once you reach this limit, your insurance will cover 100% of the costs for the rest of the year, 
          and you won’t have to pay any more copays or coinsurance.`
        }
      );
    }
  }

  setDisclaimerMessage(clientPayingFullOrCoinsurance: boolean): void {
    if (clientPayingFullOrCoinsurance) {
      this.disclaimerMessage = `The rate for each session may vary depending on its duration, 
        with initial sessions often costing more than follow-up sessions. 
        This is only an estimate and does not guarantee coverage or payment. 
        All claims are subject to the terms and conditions of your insurance plan at the time of service.`;
    } else {
      this.disclaimerMessage = `This is only an estimate and does not guarantee coverage or payment. 
      All claims are subject to the terms and conditions of your insurance plan at the time of service.`;
    }
  }
}
